import React, { useEffect, useState } from "react";
import { ArrowRight, Shield, Target, UserCog } from "lucide-react";
import { api } from "../../lib/api";
import { descFor } from "../../lib/inventoryOptions";
import { ArchetypeLine } from "./ArchetypeLine";

const pick = (sel, field) => ((sel || {})[field] || [])[0]?.text || "";
const list = (sel, field) => ((sel || {})[field] || []).map(s => s.text);
const hasAny = (sel) => ["off_archetype", "def_archetype", "team_role", "team_level", "role_responsibilities"].some(f => ((sel || {})[f] || []).length);

function Arch({ icon: Icon, label, value, target, color }) {
  const show = target && target !== value;
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575] flex items-center gap-1.5"><Icon className="w-3 h-3" style={{ color }} /> {label}</div>
      <div className="text-sm text-white mt-0.5 flex flex-wrap items-center gap-1.5">
        <span title={descFor(label.startsWith("Off") ? "off_archetype" : "def_archetype", value) || undefined}>{value || <span className="text-[#575757] italic text-xs">—</span>}</span>
        {show && <><ArrowRight className="w-3 h-3 text-[#757575]" /><span className="font-bold" style={{ color }} title={descFor(label.startsWith("Off") ? "off_archetype" : "def_archetype", target) || undefined}>{target}</span></>}
      </div>
    </div>
  );
}

function Column({ who, sel, when, archetypes, showArch }) {
  const legacy = { offense: pick(sel, "off_archetype"), defense: pick(sel, "def_archetype") };
  return (
    <div className="min-w-0 space-y-3" data-testid={`archetype-col-${(who || "latest").toLowerCase()}`}>
      {who && <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--org-accent)]">{who}{when ? <span className="text-[#757575] font-normal ml-2">{when}</span> : null}</div>}
      {showArch && (archetypes?.offense?.primary || archetypes?.defense?.primary
        ? <ArchetypeLine archetypes={archetypes} testid="home-archetype-line" />
        : <>
          <Arch icon={Target} label="Offensive Archetype" value={legacy.offense} target={pick(sel, "target_off_archetype")} color="#f0a35b" />
          <Arch icon={Shield} label="Defensive Archetype" value={legacy.defense} target={pick(sel, "target_def_archetype")} color="#5b8ff0" />
        </>)}
      <div>
        <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575] flex items-center gap-1.5"><UserCog className="w-3 h-3 text-[#d8b854]" /> Current Team Role</div>
        <div className="text-sm text-white mt-0.5">{pick(sel, "team_role") || <span className="text-[#575757] italic text-xs">—</span>}{pick(sel, "team_level") && <span className="text-[#a0a0a0]"> · {pick(sel, "team_level")}</span>}</div>
      </div>
      {list(sel, "role_responsibilities").length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {list(sel, "role_responsibilities").map(t => <span key={t} className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 border border-white/15 text-[#c0c0c0]" data-testid="archetype-responsibility">{t}</span>)}
        </div>
      )}
    </div>
  );
}

// Compact "Archetype & Role" summary. When different people (Player / Coach / …) have saved an inventory, show the latest from each side by side.
export function ArchetypeRoleCard({ playerId, snapshot, archetypes }) {
  const [cols, setCols] = useState(null);
  useEffect(() => {
    let live = true;
    api.getSnapshotVersions(playerId).then(vs => {
      if (!live) return;
      const byWho = new Map();
      for (const v of vs.filter(v => v.section === "inventory")) {
        const f = v.fields || {};
        const who = f.completed_by || "";
        if (who && !byWho.has(who) && hasAny(f.inventory_selections)) byWho.set(who, { who, sel: f.inventory_selections, when: new Date(v.saved_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) });
      }
      setCols([...byWho.values()]);
    }).catch(() => setCols([]));
    return () => { live = false; };
  }, [playerId]);

  const latest = snapshot?.inventory_selections;
  if (!cols) return null;
  const hasNew = !!(archetypes?.offense?.primary || archetypes?.defense?.primary);
  let columns = cols.length > 1 ? cols : (hasAny(latest) ? [{ who: snapshot?.completed_by || "", sel: latest, when: "" }] : []);
  if (!columns.length && hasNew) columns = [{ who: "", sel: {}, when: "" }];
  if (!columns.length) return null;
  return (
    <div className="border border-white/10 bg-[#141414] p-4" data-testid="archetype-role-card">
      <div className="text-[9px] uppercase tracking-[0.35em] font-bold text-[var(--org-accent)] mb-3">Archetype &amp; Role{columns.length > 1 ? " · side by side" : ""}</div>
      <div className={`grid gap-5 ${columns.length > 1 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
        {columns.map((c, i) => <Column key={c.who || "latest"} {...c} archetypes={archetypes} showArch={i === 0 || !hasNew} />)}
      </div>
    </div>
  );
}
